import React from 'react'
import styled from 'styled-components'
import { hideModal } from './modalActions'
import { connect } from 'react-redux'
import PropTypes  from 'prop-types'


/**
 * InnerModal is styled by the StyledInnerModal common component
 * @param {onClick} function onClick function can be passed to the InnerModal to override the default behavior of
 * closing the modal if the click target has a 'close' id.
 * @param {className} string passed through to child for styled component styling
 */
export const InnerModal = ({className, children, onClick})=>(
  <div className={className} onClick={(e) => onClick ? onClick(e) : e.target.id !=='close' && e.stopPropagation()}>
    {children}
  </div>
)
InnerModal.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func
}

const StyledModal = styled.div`
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    position: fixed;
    height: auto;
    width: auto;
    z-index: 5;
    background-color: #00000070;
    outline: none;
`
/**
 * Several options are available to use this modal component:
 * 1. Implementer controlled. The default export (ConnectedModal) is used without a 'type' prop passed in.
 *  Showing vs. hiding the modal is done outside of the modal component as long as modal state is true.
 * 2. Use ConnectedModal with type: the component looks at given type compared to the modalType in
 *  state to determine if the modal should be rendered. If the modal component is used in this way, the state
 *  that wraps the component will be maintained
 * 3. ModalHOC: Wrap the modal child component, with or without a type, to have the component rendered as a model
 *    without having to use the Modal component directly. This method avoids maintaining state.
 *
 * Modal connected props ( modalState, modalType, hideModal ) are passed to modal children  to avoid prop drilling
 *
 */
const Modal = ({ modalState, modalType, children, type, hideModal }) => {
  // use state logic to determine if model is shown if a type is passed in.
  // else assumed model is displayed manually
  const showModal = modalState && ((modalType &&  modalType === type) || (!type))
  const handleHideModal = () => {
    // hide modal if active, and modal has either a type property or no type
    if (showModal) {
      hideModal()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      handleHideModal()
    }
  }

  return (
    showModal &&
    <StyledModal
      onClick={handleHideModal}
      onKeyDown={handleKeyDown}
      tabIndex='0'
    >
      {/* Pass through Modal handling props */}
      {React.Children.map(children, child=>React.cloneElement(child, {
        modalState: modalState,
        modalType: modalType,
        hideModal: hideModal
      }))}
    </StyledModal>
  )
}

Modal.propTypes = {
  modalState: PropTypes.bool,
  modalType: PropTypes.string,
  type: PropTypes.string,
  hideModal: PropTypes.func
}
export const ConnectedModal = connect(store=>(
  {
    modalState: store.modalReducer.modalState,
    modalType: store.modalReducer.modalType
  }
), {hideModal})(Modal)

export default ConnectedModal


//
export const ModalHOC = (Component, type) => (props) => (
  <ConnectedModal type={type}>
    <Component {...props}/>
  </ConnectedModal>
)
